"use client";
import * as React from "react";

import { Button as HeadlessButton } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/helpers/cn.helper";


const baseClasses = cn(
  "flex items-center justify-center",
  "cursor-pointer",
  "selection:bg-transparent",
  "disabled:pointer-events-none",
);

const iconButtonVariants = cva(baseClasses, {
  variants: {
    variant: {
      primary: [
        "h-10 max-h-10 min-h-10 w-10 min-w-10 max-w-10",
        "text-light-400 rounded-lg bg-transparent",
        "hover:text-light-600",
        "active:bg-light-100 active:text-light-600",
        'data-[active="true"]:bg-light-100 data-[active="true"]:text-light-600',
        "disabled:text-light-200 disabled:bg-transparent",
      ],
      secondary: [
        "h-7 max-h-7 min-h-7 w-7 min-w-7 max-w-7",
        "text-light-400 rounded-lg bg-transparent",
        "hover:text-light-600",
        "active:bg-light-100 active:text-light-600",
        'data-[active="true"]:bg-light-100 data-[active="true"]:text-light-600',
        "disabled:text-light-200 disabled:bg-transparent",
      ],
      special: [
        "h-10 max-h-10 min-h-10 w-10 min-w-10 max-w-10",
        "bg-light-600 text-white",
        "hover:bg-light-700",
        "active:bg-light-800",
        "disabled:bg-light-50",
      ],
      mapToolbar: [
        "group h-12 min-h-12 w-12 min-w-12 shrink-0 rounded-lg border border-gray-100 bg-white text-gray-600 shadow-sm",
        "hover:bg-gray-100 hover:text-gray-800",
        "data-[active]:bg-gray-100 data-[hover]:bg-gray-100",
        "disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400",
      ],
    },
  },

  defaultVariants: {
    variant: "primary",
  },
});
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
  as?: "button" | "a";
    icon: React.ElementType;
    width?: number;
    height?: number;
    iconClassName?: string;
}
const IconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      disabled,
      as = "button",
      type = "button",
      icon: Icon,
      width = 24,
      height = 24,
      iconClassName,
      ...props
    },
    ref,
  ) => {
    return (
      <HeadlessButton
        ref={ref}
        as={as}
        className={cn(iconButtonVariants({ variant, className }))}
        disabled={disabled}
        type={type}
        {...props}
      >
        <Icon
          className={cn("pointer-events-none text-inherit", iconClassName)}
          height={height}
          width={width}
        />
      </HeadlessButton>
    );
  },
);
IconButton.displayName = "IconButton";
export default IconButton;
export { IconButton, iconButtonVariants };
